import { useState, useMemo, useRef } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, DollarSign, FileDown, FileUp, Download } from "lucide-react";
import * as XLSX from 'xlsx';

export default function CommissionsConfig() {
  const { user } = useGoogleAuth();
  const [activeTab, setActiveTab] = useState("item");

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold">Configuração de Comissões</h1>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="item">Comissão por Item</TabsTrigger>
          <TabsTrigger value="cliente">Comissão por Cliente</TabsTrigger>
          <TabsTrigger value="relatorio">Relatório de Comissão</TabsTrigger>
        </TabsList>
        <TabsContent value="item">
          <ComissaoItemTab />
        </TabsContent>
        <TabsContent value="cliente">
          <ComissaoClienteTab />
        </TabsContent>
        <TabsContent value="relatorio">
          <RelatorioComissaoTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── Tab: Comissão por Item ───────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

function ComissaoItemTab() {
  const { user } = useGoogleAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [newProductCod, setNewProductCod] = useState("");
  const [newPercentual, setNewPercentual] = useState("");
  const [selectedEmitenteId, setSelectedEmitenteId] = useState<number | null>(null);

  const { data: comissoes = [], refetch } = trpc.comissoes.itemList.useQuery();
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const { data: products = [] } = trpc.products.list.useQuery();

  const upsertMutation = trpc.comissoes.itemUpsert.useMutation({
    onSuccess: () => { refetch(); toast.success("Comissão salva com sucesso!"); },
    onError: (err) => toast.error(err.message),
  });
  const deleteMutation = trpc.comissoes.itemDelete.useMutation({
    onSuccess: () => { refetch(); toast.success("Comissão removida!"); },
    onError: (err) => toast.error(err.message),
  });

  const emitenteId = (user as any)?.emitenteId ?? selectedEmitenteId;

  const filteredComissoes = useMemo(() => {
    let list = comissoes as any[];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter((c: any) =>
        c.productCod?.toLowerCase().includes(term) ||
        c.productDescription?.toLowerCase().includes(term)
      );
    }
    return list;
  }, [comissoes, searchTerm]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAdd = () => {
    if (!emitenteId) { toast.error("Selecione um emitente."); return; }
    if (!newProductCod.trim()) { toast.error("Informe o código do produto."); return; }
    const pct = parseFloat(newPercentual);
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("Percentual deve ser entre 0 e 100."); return; }
    upsertMutation.mutate({ emitenteId, productCod: newProductCod.trim().toUpperCase(), percentual: pct });
    setNewProductCod("");
    setNewPercentual("");
  };

  const handleExportExcel = () => {
    const data = (comissoes as any[]).map((c: any) => ({
      'Código do Produto': c.productCod,
      'Descrição': c.productDescription || '',
      'Comissão %': Number(c.percentual),
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Comissao por Item");
    XLSX.writeFile(wb, `comissao_item_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Exportado com sucesso!");
  };

  const handleDownloadModelo = () => {
    const data = [{ 'Código do Produto': 'EXEMPLO001', 'Comissão %': 5.00 }];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Modelo");
    XLSX.writeFile(wb, 'modelo_comissao_item.xlsx');
    toast.success("Modelo baixado!");
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!emitenteId) { toast.error("Selecione um emitente antes de importar."); return; }
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet);
      if (jsonData.length === 0) { toast.error("Arquivo vazio."); return; }
      const firstRow = jsonData[0];
      if (!('Código do Produto' in firstRow) || !('Comissão %' in firstRow)) {
        toast.error("Colunas obrigatórias: 'Código do Produto', 'Comissão %'");
        return;
      }
      let count = 0;
      for (const row of jsonData) {
        const cod = String(row['Código do Produto'] || '').trim().toUpperCase();
        const pct = parseFloat(row['Comissão %']);
        if (cod && !isNaN(pct) && pct >= 0 && pct <= 100) {
          await upsertMutation.mutateAsync({ emitenteId, productCod: cod, percentual: pct });
          count++;
        }
      }
      toast.success(`${count} comissões importadas com sucesso!`);
      refetch();
    } catch (err: any) {
      toast.error("Erro ao importar: " + (err?.message || "erro desconhecido"));
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-4 mt-4">
      {/* Filtro de emitente (só admin) */}
      {!(user as any)?.emitenteId && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Emitente:</label>
          <Select value={selectedEmitenteId?.toString() ?? ""} onValueChange={(v) => setSelectedEmitenteId(Number(v))}>
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Selecione o emitente" />
            </SelectTrigger>
            <SelectContent>
              {(emitentes as any[]).map((e: any) => (
                <SelectItem key={e.id} value={e.id.toString()}>{e.razaoSocial}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Adicionar nova comissão */}
      <Card>
        <CardHeader><CardTitle className="text-base">Adicionar Comissão por Item</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-sm font-medium">Código do Produto</label>
              <Input
                value={newProductCod}
                onChange={(e) => setNewProductCod(e.target.value)}
                placeholder="Ex: PROD001"
                list="product-list"
              />
              <datalist id="product-list">
                {(products as any[]).map((p: any) => (
                  <option key={p.id} value={p.cod}>{p.descricao}</option>
                ))}
              </datalist>
            </div>
            <div className="w-[150px]">
              <label className="text-sm font-medium">Comissão %</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={newPercentual}
                onChange={(e) => setNewPercentual(e.target.value)}
                placeholder="Ex: 5.00"
              />
            </div>
            <Button onClick={handleAdd} disabled={upsertMutation.isPending}>
              <Plus className="w-4 h-4 mr-1" /> Adicionar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Botões Excel */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleDownloadModelo}>
          <Download className="w-4 h-4 mr-1" /> Modelo
        </Button>
        <Button variant="outline" size="sm" onClick={handleExportExcel}>
          <FileDown className="w-4 h-4 mr-1" /> Exportar
        </Button>
        <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
          <FileUp className="w-4 h-4 mr-1" /> Importar
        </Button>
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportExcel} />
      </div>

      {/* Busca */}
      <Input
        placeholder="Buscar por código ou descrição..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="max-w-sm"
      />

      {/* Tabela */}
      <div className="border rounded-md overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-2">Código</th>
              <th className="text-left p-2">Descrição</th>
              <th className="text-right p-2">Comissão %</th>
              <th className="text-center p-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredComissoes.length === 0 ? (
              <tr><td colSpan={4} className="text-center p-4 text-muted-foreground">Nenhuma comissão cadastrada.</td></tr>
            ) : (
              filteredComissoes.map((c: any) => (
                <tr key={c.id} className="border-t">
                  <td className="p-2 font-mono">{c.productCod}</td>
                  <td className="p-2">{c.productDescription || "—"}</td>
                  <td className="p-2 text-right">{Number(c.percentual).toFixed(2)}%</td>
                  <td className="p-2 text-center">
                    <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate({ id: c.id })}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── Tab: Comissão por Cliente ────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

function ComissaoClienteTab() {
  const { user } = useGoogleAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [newDestinatarioId, setNewDestinatarioId] = useState<string>("");
  const [newPercentual, setNewPercentual] = useState("");
  const [selectedEmitenteId, setSelectedEmitenteId] = useState<number | null>(null);

  const { data: comissoes = [], refetch } = trpc.comissoes.clienteList.useQuery();
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const { data: destinatariosResponse } = trpc.destinatarios.list.useQuery();
  const destinatarios = (destinatariosResponse as any)?.data ?? [];

  const upsertMutation = trpc.comissoes.clienteUpsert.useMutation({
    onSuccess: () => { refetch(); toast.success("Comissão salva com sucesso!"); },
    onError: (err) => toast.error(err.message),
  });
  const deleteMutation = trpc.comissoes.clienteDelete.useMutation({
    onSuccess: () => { refetch(); toast.success("Comissão removida!"); },
    onError: (err) => toast.error(err.message),
  });

  const emitenteId = (user as any)?.emitenteId ?? selectedEmitenteId;

  const filteredComissoes = useMemo(() => {
    let list = comissoes as any[];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter((c: any) =>
        c.destinatarioNome?.toLowerCase().includes(term)
      );
    }
    return list;
  }, [comissoes, searchTerm]);

  const fileInputRefCliente = useRef<HTMLInputElement>(null);

  const handleAdd = () => {
    if (!emitenteId) { toast.error("Selecione um emitente."); return; }
    if (!newDestinatarioId) { toast.error("Selecione um destinatário."); return; }
    const pct = parseFloat(newPercentual);
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("Percentual deve ser entre 0 e 100."); return; }
    upsertMutation.mutate({ emitenteId, destinatarioId: Number(newDestinatarioId), percentual: pct });
    setNewDestinatarioId("");
    setNewPercentual("");
  };

  const handleExportExcel = () => {
    const data = (comissoes as any[]).map((c: any) => ({
      'ID Destinatário': c.destinatarioId,
      'Razão Social': c.destinatarioNome || '',
      'Comissão %': Number(c.percentual),
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Comissao por Cliente");
    XLSX.writeFile(wb, `comissao_cliente_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Exportado com sucesso!");
  };

  const handleDownloadModelo = () => {
    const data = [{ 'ID Destinatário': 1, 'Razão Social (referência)': 'EXEMPLO LTDA', 'Comissão %': 3.00 }];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Modelo");
    XLSX.writeFile(wb, 'modelo_comissao_cliente.xlsx');
    toast.success("Modelo baixado!");
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!emitenteId) { toast.error("Selecione um emitente antes de importar."); return; }
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet);
      if (jsonData.length === 0) { toast.error("Arquivo vazio."); return; }
      const firstRow = jsonData[0];
      if (!('ID Destinatário' in firstRow) || !('Comissão %' in firstRow)) {
        toast.error("Colunas obrigatórias: 'ID Destinatário', 'Comissão %'");
        return;
      }
      let count = 0;
      for (const row of jsonData) {
        const destId = parseInt(row['ID Destinatário']);
        const pct = parseFloat(row['Comissão %']);
        if (!isNaN(destId) && destId > 0 && !isNaN(pct) && pct >= 0 && pct <= 100) {
          await upsertMutation.mutateAsync({ emitenteId, destinatarioId: destId, percentual: pct });
          count++;
        }
      }
      toast.success(`${count} comissões importadas com sucesso!`);
      refetch();
    } catch (err: any) {
      toast.error("Erro ao importar: " + (err?.message || "erro desconhecido"));
    }
    if (fileInputRefCliente.current) fileInputRefCliente.current.value = '';
  };

  return (
    <div className="space-y-4 mt-4">
      {/* Filtro de emitente (só admin) */}
      {!(user as any)?.emitenteId && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Emitente:</label>
          <Select value={selectedEmitenteId?.toString() ?? ""} onValueChange={(v) => setSelectedEmitenteId(Number(v))}>
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Selecione o emitente" />
            </SelectTrigger>
            <SelectContent>
              {(emitentes as any[]).map((e: any) => (
                <SelectItem key={e.id} value={e.id.toString()}>{e.razaoSocial}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Adicionar nova comissão */}
      <Card>
        <CardHeader><CardTitle className="text-base">Adicionar Comissão por Cliente</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-sm font-medium">Destinatário</label>
              <Select value={newDestinatarioId} onValueChange={setNewDestinatarioId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o destinatário" />
                </SelectTrigger>
                <SelectContent>
                  {(destinatarios as any[]).map((d: any) => (
                    <SelectItem key={d.id} value={d.id.toString()}>{d.razaoSocial}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[150px]">
              <label className="text-sm font-medium">Comissão %</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={newPercentual}
                onChange={(e) => setNewPercentual(e.target.value)}
                placeholder="Ex: 3.00"
              />
            </div>
            <Button onClick={handleAdd} disabled={upsertMutation.isPending}>
              <Plus className="w-4 h-4 mr-1" /> Adicionar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Botões Excel */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleDownloadModelo}>
          <Download className="w-4 h-4 mr-1" /> Modelo
        </Button>
        <Button variant="outline" size="sm" onClick={handleExportExcel}>
          <FileDown className="w-4 h-4 mr-1" /> Exportar
        </Button>
        <Button variant="outline" size="sm" onClick={() => fileInputRefCliente.current?.click()}>
          <FileUp className="w-4 h-4 mr-1" /> Importar
        </Button>
        <input ref={fileInputRefCliente} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportExcel} />
      </div>

      {/* Busca */}
      <Input
        placeholder="Buscar por nome do destinatário..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="max-w-sm"
      />

      {/* Tabela */}
      <div className="border rounded-md overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-2">Destinatário</th>
              <th className="text-right p-2">Comissão %</th>
              <th className="text-center p-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredComissoes.length === 0 ? (
              <tr><td colSpan={3} className="text-center p-4 text-muted-foreground">Nenhuma comissão cadastrada.</td></tr>
            ) : (
              filteredComissoes.map((c: any) => (
                <tr key={c.id} className="border-t">
                  <td className="p-2">{c.destinatarioNome || `ID ${c.destinatarioId}`}</td>
                  <td className="p-2 text-right">{Number(c.percentual).toFixed(2)}%</td>
                  <td className="p-2 text-center">
                    <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate({ id: c.id })}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── Tab: Relatório de Comissão ───────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

function RelatorioComissaoTab() {
  const { user } = useGoogleAuth();
  const now = new Date();
  const currentMonthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const currentMonthEndStr = `${currentMonthEnd.getFullYear()}-${String(currentMonthEnd.getMonth() + 1).padStart(2, "0")}-${String(currentMonthEnd.getDate()).padStart(2, "0")}`;

  const [vendedorFilter, setVendedorFilter] = useState<string>("__all__");
  const [periodoFilter, setPeriodoFilter] = useState<"atual" | "anterior" | "custom">("atual");
  const [customMonth, setCustomMonth] = useState(String(now.getMonth() + 1).padStart(2, "0"));
  const [customYear, setCustomYear] = useState(String(now.getFullYear()));

  const getDateRange = () => {
    if (periodoFilter === "atual") {
      return { startDate: currentMonthStart, endDate: currentMonthEndStr };
    } else if (periodoFilter === "anterior") {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const prevEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        startDate: `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, "0")}-01`,
        endDate: `${prevEnd.getFullYear()}-${String(prevEnd.getMonth() + 1).padStart(2, "0")}-${String(prevEnd.getDate()).padStart(2, "0")}`,
      };
    } else {
      const m = parseInt(customMonth);
      const y = parseInt(customYear);
      const end = new Date(y, m, 0);
      return {
        startDate: `${y}-${String(m).padStart(2, "0")}-01`,
        endDate: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`,
      };
    }
  };

  const dateRange = getDateRange();

  const { data: vendedores = [] } = trpc.comissoes.vendedores.useQuery();
  const { data: relatorio = [] } = trpc.comissoes.relatorio.useQuery({
    vendedor: vendedorFilter === "__all__" ? undefined : vendedorFilter,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  // Calcular totais por vendedor
  const totaisPorVendedor = useMemo(() => {
    const map: Record<string, number> = {};
    (relatorio as any[]).forEach((item: any) => {
      const vendedor = item.vendedor || "Sem vendedor";
      const comissaoPercentual = parseFloat(item.comissaoPercentual) || 0;
      const valorComImpostos = parseFloat(item.valorComImpostos) || 0;
      const comissaoTotal = (comissaoPercentual / 100) * valorComImpostos;
      map[vendedor] = (map[vendedor] || 0) + comissaoTotal;
    });
    return map;
  }, [relatorio]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
  };

  return (
    <div className="space-y-4 mt-4">
      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-sm font-medium">Vendedor</label>
          <Select value={vendedorFilter} onValueChange={setVendedorFilter}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Todos os vendedores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {(vendedores as string[]).map((v) => (
                <SelectItem key={v} value={v}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium">Período</label>
          <Select value={periodoFilter} onValueChange={(v) => setPeriodoFilter(v as any)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="atual">Mês Atual</SelectItem>
              <SelectItem value="anterior">Mês Anterior</SelectItem>
              <SelectItem value="custom">Outro Período</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {periodoFilter === "custom" && (
          <>
            <div>
              <label className="text-sm font-medium">Mês</label>
              <Select value={customMonth} onValueChange={setCustomMonth}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1).padStart(2, "0")}>
                      {new Date(2000, i).toLocaleString("pt-BR", { month: "long" })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Ano</label>
              <Select value={customYear} onValueChange={setCustomYear}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = now.getFullYear() - i;
                    return <SelectItem key={year} value={String(year)}>{year}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </div>

      {/* Cards de total por vendedor */}
      {Object.keys(totaisPorVendedor).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Object.entries(totaisPorVendedor).map(([vendedor, total]) => (
            <Card key={vendedor}>
              <CardContent className="p-4 flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{vendedor}</p>
                  <p className="text-lg font-bold">{formatCurrency(total)}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tabela do relatório */}
      <div className="border rounded-md overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-2">Cód Item</th>
              <th className="text-left p-2">Descrição</th>
              <th className="text-center p-2">Pedido</th>
              <th className="text-right p-2">Valor s/ Impostos</th>
              <th className="text-right p-2">Valor c/ Impostos</th>
              <th className="text-left p-2">Vendedor</th>
              <th className="text-right p-2">Comissão %</th>
              <th className="text-right p-2">Comissão R$ Unit.</th>
              <th className="text-right p-2">Comissão R$ Total</th>
            </tr>
          </thead>
          <tbody>
            {(relatorio as any[]).length === 0 ? (
              <tr><td colSpan={9} className="text-center p-4 text-muted-foreground">Nenhum dado encontrado para o período selecionado.</td></tr>
            ) : (
              (relatorio as any[]).map((item: any, idx: number) => {
                const comissaoPercentual = parseFloat(item.comissaoPercentual) || 0;
                const valorSemImpostos = parseFloat(item.valorSemImpostos) || 0;
                const valorComImpostos = parseFloat(item.valorComImpostos) || 0;
                const valorUnitario = parseFloat(item.valorUnitario) || 0;
                const quantidade = parseFloat(item.quantidade) || 0;
                const comissaoUnitario = (comissaoPercentual / 100) * valorUnitario;
                const comissaoTotal = (comissaoPercentual / 100) * valorComImpostos;
                return (
                  <tr key={idx} className="border-t">
                    <td className="p-2 font-mono">{item.productCod}</td>
                    <td className="p-2">{item.productDescription}</td>
                    <td className="p-2 text-center">#{item.pedidoNumero}</td>
                    <td className="p-2 text-right">{formatCurrency(valorSemImpostos)}</td>
                    <td className="p-2 text-right">{formatCurrency(valorComImpostos)}</td>
                    <td className="p-2">{item.vendedor || "—"}</td>
                    <td className="p-2 text-right">{comissaoPercentual.toFixed(2)}%</td>
                    <td className="p-2 text-right">{formatCurrency(comissaoUnitario)}</td>
                    <td className="p-2 text-right font-medium">{formatCurrency(comissaoTotal)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
